import { PixelBuffer, mix, rgb } from "../../intro/pixel_raster"
import { ArtRect, CreationLayout } from "./creation_layout"
import { Choice, HairPage, LEFT_TABS, LeftTab, RIGHT_TABS, RightTab, SheetMode, SheetOption, SheetRow } from "./creation_sheet"
import { cellPosition, layoutGrid } from "./grid"
import { PICTURE, PictureTrait } from "./pictures"

// The character-creation panel, the turn plates beside the figure and the way on, as pixel art on the
// intro's grid. Painting returns the buffers, where each control is (in art pixels of the stage), and the
// words to set over them in Jost -- the page owns the text, these only say what and where.
//
// Pure: the sheet's rows in, buffers and data out.

const C = {
    panel: rgb(0x0b0e16, 222),
    edge: rgb(0x744b30),
    edgeShade: mix(rgb(0x744b30), rgb(0x0b0b13), 0.25),
    lip: rgb(0x996b4a, 90),
    outline: rgb(0x0b0b13, 150),
    rivet: rgb(0xb78c41),
    rivetHi: rgb(0xd9b75c),
    rivetLo: rgb(0x4b2b13),
    tile: rgb(0x10141f),
    tileHover: rgb(0x171c2b),
    tileLit: rgb(0x2a2016),
    tileEdge: rgb(0x2e2533),
    gold: rgb(0xd9b75c),
    goldHi: rgb(0xffd27a),
    bronze: rgb(0x744b30),
    bronzeDot: rgb(0x744b30, 120),
    seg: rgb(0x151b2a),
    segOn: rgb(0x3a2a14),
    segEdge: rgb(0x3d3748),
    plate: rgb(0x0b0e16, 150),
    plateHover: rgb(0x0b0e16, 230),
    amber: rgb(0xf2a94e),
    amberHover: mix(rgb(0xf2a94e), rgb(0xfff6e8), 0.12),
    amberHi: rgb(0xffd27a),
    amberLo: rgb(0xb8742e),
    amberLine: rgb(0x4b2b13),
    sheen: rgb(0xffffff, 14),
    dieRim: rgb(0x726b7e),
    dieFace: rgb(0xc4b59f),
    dieLit: rgb(0xe5e6c7),
    pip: rgb(0x2e2533),
    ink: rgb(0x0b0b13),
    white: rgb(0xfff6e8),
}

export const TEXT = 0xfff3e0
export const TEXT_DIM = 0xb49b83
export const TEXT_GOLD = 0xe9c27a
export const TEXT_LINK = 0xd9b27a
export const TEXT_INK = 0x2a1c0c
export const TEXT_SOFT = 0xe8dcc6

export type PanelAction =
    | { type: "pick"; choice: Choice }
    | { type: "hairStep"; direction: number }
    | { type: "openGrid" }
    | { type: "closeGrid" }
    | { type: "rollLook" }
    | { type: "rollOutfit" }
    | { type: "turn"; direction: number }
    | { type: "journey" }
    | { type: "tab"; trait: LeftTab | RightTab }
    | { type: "hairPage"; direction: number; perPage: number }

export interface Hit extends ArtRect {
    id: string
    action: PanelAction
    /** The choice this control names while the pointer is over it. */
    choice?: Choice
    /** The hair row: the mouse wheel steps it. */
    hairRow?: boolean
}

/** Words to set over the art. Positions are logical pixels of the stage. */
export interface TextLine {
    key: string
    text: string
    x: number
    y: number
    size: number
    weight: "400" | "500" | "600"
    color: number
    align?: "left" | "center" | "right"
    /** Centred on `y` rather than hanging from it. */
    middle?: boolean
    /** Tracked capitals, as the intro's tagline is set. */
    caps?: boolean
    /** Shrink, then cut with an ellipsis, so it ends before this x. */
    fitTo?: number
    /** Sit just after this line (its key), on the same baseline. */
    follows?: string
}

export interface PanelView {
    mode: SheetMode
    rows: SheetRow[]
    hairWindow: SheetOption[]
    gridOptions: SheetOption[]
    /** The id of the control under the pointer. */
    hover?: string
    /** The two dice on the roll buttons: the face each shows and how far it is off the panel. */
    dice: { look: { face: number; hop: number }; outfit: { face: number; hop: number } }
    picture: (trait: PictureTrait, value: string) => Uint32Array | undefined
    /** Phone only: which trait each panel shows, and a page of the hair grid. */
    tabs?: { left: LeftTab; right: RightTab }
    hairPage?: (perPage: number) => HairPage
}

export interface PanelArt {
    panel: PixelBuffer
    cta: PixelBuffer
    turn: PixelBuffer
    hits: Hit[]
    texts: TextLine[]
}

const TILE = PICTURE + 2
const TILE_GAP = 2
const SWATCH = 7
const SWATCH_STEP = 9
const GRID_COLS = 8
const ART_PX = 2

export const choiceId = (choice: Choice): string => `${choice.trait}:${choice.kind}:${choice.value}`

const darker = (c: number, t: number) => mix(c, C.ink, t)
const lighter = (c: number, t: number) => mix(c, C.white, t)

/** A box with its corners cut: the pixel way to round a corner. */
const box = (buf: PixelBuffer, x: number, y: number, w: number, h: number, fill: number, edge: number) => {
    for (let j = 0; j < h; j++) {
        for (let i = 0; i < w; i++) {
            if ((i === 0 || i === w - 1) && (j === 0 || j === h - 1)) continue
            const rim = i === 0 || j === 0 || i === w - 1 || j === h - 1
            buf.blend(x + i, y + j, rim ? edge : fill, ((rim ? edge : fill) >>> 24) / 255)
        }
    }
}

const ring = (buf: PixelBuffer, x: number, y: number, w: number, h: number, c: number) => {
    for (let i = 1; i < w - 1; i++) {
        buf.set(x + i, y, c)
        buf.set(x + i, y + h - 1, c)
    }
    for (let j = 1; j < h - 1; j++) {
        buf.set(x, y + j, c)
        buf.set(x + w - 1, y + j, c)
    }
}

const chevron = (buf: PixelBuffer, x: number, y: number, direction: number, c: number) => {
    for (let k = 0; k < 3; k++) {
        const dx = direction > 0 ? k : 2 - k
        buf.set(x + dx, y + k, c)
        buf.set(x + dx, y + 4 - k, c)
    }
}

const DIE_PIPS: Record<number, [number, number][]> = {
    1: [[3, 3]],
    2: [[1, 1], [5, 5]],
    3: [[1, 1], [3, 3], [5, 5]],
    4: [[1, 1], [5, 1], [1, 5], [5, 5]],
    5: [[1, 1], [5, 1], [3, 3], [1, 5], [5, 5]],
    6: [[1, 1], [5, 1], [1, 3], [5, 3], [1, 5], [5, 5]],
}

const die = (buf: PixelBuffer, x: number, y: number, face: number) => {
    for (let j = 0; j < 7; j++) {
        for (let i = 0; i < 7; i++) {
            if ((i === 0 || i === 6) && (j === 0 || j === 6)) continue
            const rim = i === 0 || j === 0 || i === 6 || j === 6
            buf.set(x + i, y + j, rim ? C.dieRim : j === 1 ? C.dieLit : C.dieFace)
        }
    }
    for (const [i, j] of DIE_PIPS[face] ?? DIE_PIPS[1]) buf.set(x + i, y + j, C.pip)
}

/** The panel's frame: dark glass with a bronze rim, its corners cut, brass rivets like a sea chest's. */
const frame = (buf: PixelBuffer, p: ArtRect) => {
    const { x, y, w, h } = p
    const cut = (i: number, j: number) => {
        const di = Math.min(i, w - 1 - i)
        const dj = Math.min(j, h - 1 - j)
        return di < 2 && dj < 2 && di + dj < 2
    }
    for (let j = 0; j < h; j++) {
        for (let i = 0; i < w; i++) {
            if (!cut(i, j)) buf.set(x + i, y + j, C.panel)
        }
    }
    for (let i = 2; i < w - 2; i++) {
        buf.set(x + i, y, C.edge)
        buf.set(x + i, y + h - 1, C.edgeShade)
    }
    for (let j = 2; j < h - 2; j++) {
        buf.set(x, y + j, C.edge)
        buf.set(x + w - 1, y + j, C.edgeShade)
    }
    buf.set(x + 1, y + 1, C.edge)
    buf.set(x + w - 2, y + 1, C.edge)
    buf.set(x + 1, y + h - 2, C.edgeShade)
    buf.set(x + w - 2, y + h - 2, C.edgeShade)
    for (let i = 5; i < w - 5; i++) buf.blend(x + i, y + 1, C.lip, (C.lip >>> 24) / 255)
    for (const [rx, ry] of [[x + 3, y + 3], [x + w - 5, y + 3], [x + 3, y + h - 5], [x + w - 5, y + h - 5]]) {
        buf.set(rx, ry, C.rivetHi)
        buf.set(rx + 1, ry, C.rivet)
        buf.set(rx, ry + 1, C.rivet)
        buf.set(rx + 1, ry + 1, C.rivetLo)
    }
}

/** What every part of the panel is drawn with, into one buffer, collecting controls and words as it goes. */
const painter = (buf: PixelBuffer, hits: Hit[], texts: TextLine[], view: PanelView) => {
    const hover = view.hover
    const p = {
        outline: (r: ArtRect) => {
            for (let i = 2; i < r.w - 2; i++) {
                buf.set(r.x + i, r.y - 1, C.outline)
                buf.set(r.x + i, r.y + r.h, C.outline)
            }
            for (let j = 2; j < r.h - 2; j++) {
                buf.set(r.x - 1, r.y + j, C.outline)
                buf.set(r.x + r.w, r.y + j, C.outline)
            }
            frame(buf, r)
        },
        divider: (L: number, R: number, y: number) => {
            for (let i = L; i <= R; i += 2) buf.blend(i, y, C.bronzeDot, (C.bronzeDot >>> 24) / 255)
        },
        label: (key: string, text: string, x: number, y: number) =>
            texts.push({ key, text: text.toUpperCase(), x: x * ART_PX, y: y * ART_PX, size: 8.5, weight: "600", color: TEXT_DIM, caps: true }),
        value: (key: string, row: SheetRow, fitRight: number) =>
            texts.push({ key: `${key}:value`, text: row.line, x: 0, y: 0, size: 10, weight: "400", color: row.pointing ? TEXT_GOLD : TEXT, follows: key, fitTo: fitRight * ART_PX }),
        link: (key: string, text: string, R: number, y: number, action: PanelAction, w: number) => {
            texts.push({ key, text, x: R * ART_PX, y: y * ART_PX - 1, size: 9.5, weight: "600", color: hover === key ? TEXT_GOLD : TEXT_LINK, align: "right" })
            hits.push({ id: key, x: R - w + 1, y: y - 2, w, h: 9, action })
        },
        swatch: (choice: Choice, option: SheetOption, x: number, y: number) => {
            const id = choiceId(choice)
            if (option.selected || hover === id) {
                ring(buf, x - 2, y - 2, SWATCH + 4, SWATCH + 4, option.selected ? C.gold : C.bronze)
            }
            const c = rgb(option.swatch ?? 0x808080)
            const edge = darker(c, 0.55)
            for (let j = 0; j < SWATCH; j++) {
                for (let i = 0; i < SWATCH; i++) {
                    if ((i === 0 || i === SWATCH - 1) && (j === 0 || j === SWATCH - 1)) continue
                    const rim = i === 0 || j === 0 || i === SWATCH - 1 || j === SWATCH - 1
                    buf.set(x + i, y + j, rim ? edge : j === 1 ? lighter(c, 0.22) : c)
                }
            }
            hits.push({ id, x: x - 1, y: y - 1, w: SWATCH + 2, h: SWATCH + 2, action: { type: "pick", choice }, choice })
        },
        /** A trait's colours from (x, y), `perRow` to a row. Returns the row after the last. */
        swatches: (row: SheetRow, x: number, y: number, perRow: number = Infinity): number => {
            const options = row.color?.options ?? []
            options.forEach((option, k) =>
                p.swatch({ trait: row.id, kind: "color", value: option.value }, option, x + (k % perRow) * SWATCH_STEP, y + Math.floor(k / perRow) * SWATCH_STEP),
            )
            return y + Math.ceil(options.length / perRow) * SWATCH_STEP
        },
        tile: (trait: PictureTrait, option: SheetOption, x: number, y: number, extra: Partial<Hit> = {}) => {
            const choice: Choice = { trait, kind: "style", value: option.value }
            const id = choiceId(choice)
            const hovered = hover === id
            box(buf, x, y, TILE, TILE, option.selected ? C.tileLit : hovered ? C.tileHover : C.tile, option.selected ? C.gold : hovered ? C.bronze : C.tileEdge)
            const pixels = view.picture(trait, option.value)
            if (pixels) {
                for (let j = 0; j < PICTURE; j++) {
                    for (let i = 0; i < PICTURE; i++) {
                        const c = pixels[j * PICTURE + i]
                        if (c >>> 24) buf.set(x + 1 + i, y + 1 + j, c)
                    }
                }
            } else {
                buf.set(x + 6, y + 6, C.bronze)
                buf.set(x + 7, y + 7, C.bronze)
            }
            if (option.selected) {
                buf.set(x + 6, y + TILE, C.gold)
                buf.set(x + 7, y + TILE, C.gold)
            }
            hits.push({ id, x, y, w: TILE, h: TILE, action: { type: "pick", choice }, choice, ...extra })
        },
        /** Pictures from (x, y), `perRow` to a row. Returns the row after the last. */
        tiles: (trait: PictureTrait, options: SheetOption[], x: number, y: number, perRow: number = Infinity): number => {
            options.forEach((option, k) => p.tile(trait, option, x + (k % perRow) * (TILE + TILE_GAP), y + Math.floor(k / perRow) * (TILE + TILE_GAP)))
            return y + Math.ceil(options.length / perRow) * (TILE + TILE_GAP)
        },
        /** Male and female, two boxes sharing a border. */
        build: (row: SheetRow, x: number, y: number) =>
            row.style.options.forEach((option, k) => {
                const choice: Choice = { trait: "sex", kind: "style", value: option.value }
                const id = choiceId(choice)
                const bx = x + k * 26
                const on = option.selected
                const hovered = hover === id
                box(buf, bx, y, 27, 10, on ? C.segOn : hovered ? C.tileHover : C.seg, on ? C.gold : hovered ? C.bronze : C.segEdge)
                texts.push({ key: id, text: option.label, x: (bx + 13.5) * ART_PX, y: (y + 5) * ART_PX + 0.5, size: 9.5, weight: on ? "600" : "400", color: on ? TEXT : TEXT_DIM, align: "center", middle: true })
                // Each answers for its own 26 pixels of the shared border.
                hits.push({ id, x: bx, y, w: 26, h: 10, action: { type: "pick", choice }, choice })
            }),
        rollButton: (id: string, x: number, y: number, w: number, h: number, text: string, action: PanelAction, dieState: { face: number; hop: number }) => {
            const hovered = hover === id
            box(buf, x, y, w, h, hovered ? C.tileHover : C.tile, hovered ? C.bronze : C.segEdge)
            for (let i = 2; i < w - 2; i++) buf.blend(x + i, y + 1, C.sheen, (C.sheen >>> 24) / 255)
            die(buf, x + 4, y + Math.floor((h - 7) / 2) - dieState.hop, dieState.face)
            texts.push({ key: id, text, x: (x + 12 + (w - 12) / 2) * ART_PX, y: (y + h / 2) * ART_PX + 0.5, size: 10, weight: "500", color: hovered ? TEXT : TEXT_SOFT, align: "center", middle: true })
            hits.push({ id, x, y, w, h, action })
        },
        cta: (c: ArtRect) => {
            const hovered = hover === "journey"
            for (let j = 0; j < c.h; j++) {
                for (let i = 0; i < c.w; i++) {
                    if ((i === 0 || i === c.w - 1) && (j === 0 || j === c.h - 1)) continue
                    const rim = i === 0 || j === 0 || i === c.w - 1 || j === c.h - 1
                    buf.set(c.x + i, c.y + j, rim ? C.amberLine : j === 1 ? C.amberHi : j === c.h - 2 ? C.amberLo : hovered ? C.amberHover : C.amber)
                }
            }
            texts.push({ key: "journey", text: "Journey Onward  ▸", x: (c.x + c.w / 2) * ART_PX, y: (c.y + c.h / 2) * ART_PX + 0.5, size: 11, weight: "600", color: TEXT_INK, align: "center", middle: true })
            hits.push({ id: "journey", ...c, action: { type: "journey" } })
        },
    }
    return p
}

/** The plates either side of the figure that turn it. */
const turnPlates = (layout: CreationLayout, hits: Hit[], hover?: string): PixelBuffer => {
    const plateY = layout.feetY - 26
    const leftX = layout.figX - 23
    const rightX = layout.figX + 15
    const turn = new PixelBuffer(rightX + 9 - leftX, 9, leftX, plateY)
    for (const [direction, x] of [[-1, leftX], [1, rightX]] as [number, number][]) {
        const id = direction < 0 ? "turn-left" : "turn-right"
        const hovered = hover === id
        box(turn, x, plateY, 9, 9, hovered ? C.plateHover : C.plate, hovered ? C.bronze : rgb(0x3d3748, 200))
        chevron(turn, x + 3, plateY + 2, direction, hovered ? C.goldHi : rgb(0xd9b75c, 200))
        hits.push({ id, x: x - 2, y: plateY - 2, w: 13, h: 13, action: { type: "turn", direction } })
    }
    return turn
}

export const paintPanel = (layout: CreationLayout, view: PanelView): PanelArt =>
    layout.phone ? paintPhonePanels(layout, view) : paintDesktopPanel(layout, view)

/** Desktop: one panel at the left edge holding every trait, the way on at the bottom right. */
const paintDesktopPanel = (layout: CreationLayout, view: PanelView): PanelArt => {
    const p = layout.panel
    // One pixel of outline round the panel, so the buffer is a pixel larger each side.
    const panel = new PixelBuffer(p.w + 2, p.h + 2, p.x - 1, p.y - 1)
    const hits: Hit[] = []
    const texts: TextLine[] = []
    const L = p.x + 7
    const R = p.x + p.w - 7
    const hover = view.hover
    const paint = painter(panel, hits, texts, view)
    paint.outline(p)

    const rows = Object.fromEntries(view.rows.map(row => [row.id, row])) as Record<string, SheetRow>

    if (view.mode === "hairGrid") {
        const hair = rows.hair
        const top = p.y + 7
        paint.label("hair", hair.label, L, top)
        paint.value("hair", hair, R - 22)
        paint.link("done", "Done ▸", R, top, { type: "closeGrid" }, 19)
        const options = view.gridOptions
        const grid = layoutGrid({ count: options.length, width: R - L, height: 10 * (TILE + TILE_GAP), cellHeight: TILE, minCellWidth: TILE, maxCellWidth: TILE, gapX: TILE_GAP, gapY: TILE_GAP })
        const cols = Math.min(GRID_COLS, grid.cols)
        const gridY = top + 8
        options.forEach((option, k) => {
            const at = cellPosition(k, { ...grid, cols }, TILE, TILE_GAP, TILE_GAP)
            paint.tile("hair", option, L + at.x, gridY + at.y)
        })
        const rowsUsed = Math.ceil(options.length / cols)
        paint.swatches(hair, L + 1, gridY + rowsUsed * (TILE + TILE_GAP) + 4)
    } else {
        // Build and skin, side by side.
        const top = p.y + 7
        paint.label("sex", rows.sex.label, L, top)
        paint.label("skin", rows.skin.label, L + 59, top)
        paint.value("skin", rows.skin, R)
        paint.build(rows.sex, L, top + 7)
        paint.swatches(rows.skin, L + 60, top + 9)
        paint.divider(L, R, p.y + 28)

        // Hair: a row of heads round the current style, the way to all of them, and the colours.
        const hairTop = p.y + 32
        paint.label("hair", rows.hair.label, L, hairTop)
        paint.value("hair", rows.hair, R - 24)
        paint.link("all", `All ${view.gridOptions.length} ▸`, R, hairTop, { type: "openGrid" }, 23)
        const rowY = hairTop + 7
        hits.push({ id: "hair-prev", x: L - 1, y: rowY, w: 7, h: TILE, action: { type: "hairStep", direction: -1 }, hairRow: true })
        chevron(panel, L + 1, rowY + 5, -1, hover === "hair-prev" ? C.goldHi : C.gold)
        view.hairWindow.forEach((option, k) => paint.tile("hair", option, L + 8 + k * (TILE + TILE_GAP), rowY, { hairRow: true }))
        hits.push({ id: "hair-next", x: R - 5, y: rowY, w: 7, h: TILE, action: { type: "hairStep", direction: 1 }, hairRow: true })
        chevron(panel, R - 3, rowY + 5, 1, hover === "hair-next" ? C.goldHi : C.gold)
        paint.swatches(rows.hair, L + 1, rowY + TILE + 4)
        paint.divider(L, R, hairTop + 37)

        // Shirt, legs and feet: every cut as a picture of the character wearing it, then its colours.
        let y = hairTop + 41
        for (const trait of ["shirt", "pants", "shoes"] as PictureTrait[]) {
            const row = rows[trait]
            paint.label(trait, row.label, L, y)
            paint.value(trait, row, R)
            paint.tiles(trait, row.style.options, L, y + 7)
            paint.swatches(row, L + 1, y + 7 + TILE + 4)
            if (trait !== "shoes") paint.divider(L, R, y + 36)
            y += 40
        }
    }

    // The dice: a whole new look, or a whole new outfit.
    const rollY = p.y + p.h - 18
    paint.rollButton("roll-look", L, rollY, 62, 11, "Roll look", { type: "rollLook" }, view.dice.look)
    paint.rollButton("roll-outfit", R - 62, rollY, 63, 11, "Roll outfit", { type: "rollOutfit" }, view.dice.outfit)

    // The way on.
    const c = layout.cta
    const cta = new PixelBuffer(c.w, c.h, c.x, c.y)
    painter(cta, hits, texts, view).cta(c)

    return { panel, cta, turn: turnPlates(layout, hits, hover), hits, texts }
}

/**
 * Phone: a tabbed panel either side of the figure, each showing one trait (build, skin and hair on the
 * left; shirt, legs and feet on the right), and Roll look, Journey Onward and Roll outfit along the bottom.
 */
const paintPhonePanels = (layout: CreationLayout, view: PanelView): PanelArt => {
    const left = layout.panel
    const right = layout.rightPanel
    const panel = new PixelBuffer(right.x + right.w + 2 - (left.x - 1), left.h + 2, left.x - 1, left.y - 1)
    const hits: Hit[] = []
    const texts: TextLine[] = []
    const hover = view.hover
    const paint = painter(panel, hits, texts, view)
    const rows = Object.fromEntries(view.rows.map(row => [row.id, row])) as Record<string, SheetRow>
    const tabs = view.tabs ?? { left: "sex", right: "shirt" }

    const side = (rect: ArtRect, traits: readonly (LeftTab | RightTab)[], chosen: LeftTab | RightTab) => {
        paint.outline(rect)
        const L = rect.x + 6
        const R = rect.x + rect.w - 6
        const width = R - L
        // The tabs: one per trait, the chosen one lit.
        const gap = 2
        const tabW = Math.floor((width - gap * (traits.length - 1)) / traits.length)
        traits.forEach((trait, k) => {
            const id = `tab:${trait}`
            const x = L + k * (tabW + gap)
            const on = trait === chosen
            const hovered = hover === id
            box(panel, x, rect.y + 4, tabW, 10, on ? C.segOn : hovered ? C.tileHover : C.seg, on ? C.gold : hovered ? C.bronze : C.segEdge)
            texts.push({ key: id, text: rows[trait].label, x: (x + tabW / 2) * ART_PX, y: (rect.y + 9) * ART_PX + 0.5, size: 9, weight: on ? "600" : "400", color: on ? TEXT : TEXT_DIM, align: "center", middle: true })
            hits.push({ id, x, y: rect.y + 4, w: tabW, h: 10, action: { type: "tab", trait } })
        })

        const row = rows[chosen]
        const top = rect.y + 18
        const perRow = Math.max(1, Math.floor((width + TILE_GAP) / (TILE + TILE_GAP)))
        const swatchesPerRow = Math.max(1, Math.floor((width + 2) / SWATCH_STEP))
        const content = top + 8
        if (chosen === "sex") {
            paint.label(chosen, row.label, L, top)
            paint.value(chosen, row, R)
            paint.build(row, L, content)
        } else if (chosen === "skin") {
            paint.label(chosen, row.label, L, top)
            paint.value(chosen, row, R)
            paint.swatches(row, L + 1, content + 2, swatchesPerRow)
        } else if (chosen === "hair") {
            // Every hairstyle, a page at a time, opening on the current one's page.
            const perPage = perRow * 2
            const page = view.hairPage ? view.hairPage(perPage) : { page: 0, pages: 1, options: row.style.options.slice(0, perPage) }
            paint.label(chosen, row.label, L, top)
            paint.value(chosen, row, R - 28)
            texts.push({ key: "hair-page", text: `${page.page + 1}/${page.pages}`, x: (R - 9) * ART_PX, y: top * ART_PX, size: 9, weight: "500", color: TEXT_DIM, align: "center" })
            hits.push({ id: "hair-page-prev", x: R - 26, y: top - 2, w: 8, h: 9, action: { type: "hairPage", direction: -1, perPage } })
            chevron(panel, R - 24, top + 1, -1, hover === "hair-page-prev" ? C.goldHi : C.gold)
            hits.push({ id: "hair-page-next", x: R - 1, y: top - 2, w: 8, h: 9, action: { type: "hairPage", direction: 1, perPage } })
            chevron(panel, R + 1, top + 1, 1, hover === "hair-page-next" ? C.goldHi : C.gold)
            const after = paint.tiles("hair", page.options, L, content, perRow)
            const tileRowsLeft = 2 - Math.ceil(page.options.length / perRow)
            paint.swatches(row, L + 1, after + tileRowsLeft * (TILE + TILE_GAP) + 2, swatchesPerRow)
        } else {
            paint.label(chosen, row.label, L, top)
            paint.value(chosen, row, R)
            const after = paint.tiles(chosen as PictureTrait, row.style.options, L, content, perRow)
            paint.swatches(row, L + 1, after + 2, swatchesPerRow)
        }
    }
    side(left, LEFT_TABS, tabs.left)
    side(right, RIGHT_TABS, tabs.right)

    // The bottom row: the dice either side, the way on in the middle, under the figure.
    const view_ = layout.view
    const rolls = layout.rolls
    const rowY = Math.min(rolls.look.y, layout.cta.y)
    const rowH = Math.max(rolls.look.h, layout.cta.h)
    const bottom = new PixelBuffer(view_.w, rowH, view_.x, rowY)
    const row = painter(bottom, hits, texts, view)
    row.rollButton("roll-look", rolls.look.x, rolls.look.y, rolls.look.w, rolls.look.h, "Roll look", { type: "rollLook" }, view.dice.look)
    row.rollButton("roll-outfit", rolls.outfit.x, rolls.outfit.y, rolls.outfit.w, rolls.outfit.h, "Roll outfit", { type: "rollOutfit" }, view.dice.outfit)
    row.cta(layout.cta)

    return { panel, cta: bottom, turn: turnPlates(layout, hits, hover), hits, texts }
}

/** The control under a point, topmost first. */
export const hitAt = (hits: Hit[], x: number, y: number): Hit | undefined => {
    for (let k = hits.length - 1; k >= 0; k--) {
        const h = hits[k]
        if (x >= h.x && x < h.x + h.w && y >= h.y && y < h.y + h.h) return h
    }
    return undefined
}
